import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  BadgeCheck,
  Building2,
  CheckCircle2,
  ClipboardList,
  HardHat,
  Inbox,
  Loader2,
  MessageSquare,
  Receipt,
  ShoppingCart,
  Sparkles,
  Users,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BuiltOSMark } from "@/components/common/BuilderLogo";
import { api, errMsg } from "@/lib/api";
import "@/styles/waitlist.css";

/* ------------------------------------------------------------------------------------------
 * Phase 163 — BuiltOS public waitlist landing page (the signed-out home page, "/").
 * Navy #1B2B4B · white · accent blue #2D5BE3. No nav, no social. Everything scoped under .builtos-waitlist.
 * ---------------------------------------------------------------------------------------- */

const JOB_OPTIONS = [
  { value: "1-3", label: "1–3" },
  { value: "4-10", label: "4–10" },
  { value: "10+", label: "10+" },
];

const PAIN_POINTS = [
  "Your actuals live in a spreadsheet you haven't opened since last Tuesday.",
  "Your supplier invoices are in your email, your phone, and maybe your ute.",
  "Your client wants an update and you're pulling it together at 10pm.",
];

const FEATURES = [
  { icon: Receipt, label: "Job Financials", text: "Actuals, budgets, and forecasts by cost centre, in real time." },
  { icon: Inbox, label: "Invoice Inbox", text: "Every supplier and contractor invoice, allocated and approved in one place." },
  { icon: ShoppingCart, label: "Procurement", text: "Trusted suppliers, RFQs, and purchase orders without the back-and-forth." },
  { icon: Users, label: "Client Portal", text: "Progress updates, handover countdown, and notices. No more 10pm calls." },
  { icon: Building2, label: "External Portal", text: "Suppliers, consultants, and contractors all on the same page as you." },
  { icon: Sparkles, label: "BuiltBuddy AI", text: "Ask your job anything. Get answers, not more admin." },
];

const TIERS = [
  { key: "starter", name: "Starter", price: 199, blurb: "Up to 3 active jobs. Perfect for the owner-operator." },
  { key: "builder", name: "Builder", price: 399, blurb: "Up to 10 active jobs. For builders running a real business.", popular: true },
  { key: "pro", name: "Pro", price: 799, blurb: "Unlimited jobs and team logins. Replaces your part-time admin." },
];

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/;

/* Fades a block in the first time it scrolls into view (opacity + transform only). */
const Reveal = ({ children, className = "", as: Tag = "div", ...rest }) => {
  const ref = useRef(null);
  const [on, setOn] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setOn(true);
      return undefined;
    }
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        setOn(true);
        io.disconnect();
      }
    }, { rootMargin: "0px 0px -10% 0px", threshold: 0.08 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <Tag ref={ref} className={`wl-reveal ${on ? "is-in" : ""} ${className}`} {...rest}>
      {children}
    </Tag>
  );
};

/**
 * The signup form. `variant="hero"` is the full form; `variant="footer"` is the compact repeat.
 * Both post to /api/public/waitlist and swap to a success / already-on-the-list panel.
 */
export const WaitlistForm = ({ variant = "hero", source = "home", idPrefix = "wl" }) => {
  const compact = variant === "footer";
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", active_jobs: "", website: "" });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // {duplicate, first_name}
  const [failed, setFailed] = useState("");
  const set = (k) => (v) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: "" }));
  };

  const validate = () => {
    const e = {};
    if (!form.first_name.trim()) e.first_name = "Please enter your first name.";
    if (!form.last_name.trim()) e.last_name = "Please enter your last name.";
    if (!EMAIL_RE.test(form.email.trim())) e.email = "Please enter a valid email address.";
    if (!form.active_jobs) e.active_jobs = "Pick the closest range.";
    setErrors(e);
    return !Object.keys(e).length;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    setFailed("");
    if (!validate()) return;
    setBusy(true);
    try {
      const r = await api.post("/public/waitlist", { ...form, email: form.email.trim(), source });
      setResult({ duplicate: !!r.data.duplicate, first_name: r.data.first_name || form.first_name.trim() });
      toast.success(r.data.duplicate ? "You're already on the list — discount still locked in." : "You're on the list. 20% locked in.");
    } catch (e) {
      const msg = errMsg(e, "Something went wrong. Try again.");
      setFailed(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const tid = (s) => `${idPrefix}-${s}`;
  const testPrefix = compact ? "footer-waitlist" : "waitlist";

  if (result) {
    return (
      <div
        className="flex flex-col items-start gap-3 py-2"
        role="status"
        aria-live="polite"
        data-testid={result.duplicate ? `${testPrefix}-duplicate-state` : `${testPrefix}-success-state`}
      >
        <span className="inline-flex h-11 w-11 items-center justify-center rounded-full" style={{ background: "rgba(47,191,113,0.14)" }}>
          <CheckCircle2 className="h-6 w-6" style={{ color: "rgb(47,191,113)" }} />
        </span>
        <h3 className="wl-heading text-xl font-bold sm:text-2xl">
          {result.duplicate ? "You're already on the waitlist." : "You're on the list."}
        </h3>
        <p className="text-sm sm:text-base" style={{ color: "rgb(var(--wl-text-2))" }}>
          {result.duplicate
            ? "We've already locked in your 20% lifetime discount. We'll email you the moment early access opens."
            : `Thanks, ${result.first_name}. Your 20% lifetime discount is locked in — we'll email you the moment early access opens.`}
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate className="space-y-4" data-testid={compact ? "footer-waitlist-form" : "waitlist-form"}>
      <div className={`grid gap-3 ${compact ? "sm:grid-cols-2" : "sm:grid-cols-2"}`}>
        <div className="space-y-1.5">
          <Label htmlFor={tid("first")} className="wl-label">First name</Label>
          <Input
            id={tid("first")}
            value={form.first_name}
            onChange={(e) => set("first_name")(e.target.value)}
            autoComplete="given-name"
            placeholder="Dave"
            className="wl-input"
            aria-invalid={errors.first_name ? "true" : undefined}
            aria-describedby={errors.first_name ? tid("first-err") : undefined}
            data-testid={`${testPrefix}-first-name-input`}
          />
          {errors.first_name && <p id={tid("first-err")} className="text-xs" style={{ color: "rgb(var(--wl-danger))" }} data-testid={`${testPrefix}-first-name-error`}>{errors.first_name}</p>}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={tid("last")} className="wl-label">Last name</Label>
          <Input
            id={tid("last")}
            value={form.last_name}
            onChange={(e) => set("last_name")(e.target.value)}
            autoComplete="family-name"
            placeholder="Nguyen"
            className="wl-input"
            aria-invalid={errors.last_name ? "true" : undefined}
            aria-describedby={errors.last_name ? tid("last-err") : undefined}
            data-testid={`${testPrefix}-last-name-input`}
          />
          {errors.last_name && <p id={tid("last-err")} className="text-xs" style={{ color: "rgb(var(--wl-danger))" }} data-testid={`${testPrefix}-last-name-error`}>{errors.last_name}</p>}
        </div>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor={tid("email")} className="wl-label">Email</Label>
        <Input
          id={tid("email")}
          type="email"
          inputMode="email"
          value={form.email}
          onChange={(e) => set("email")(e.target.value)}
          autoComplete="email"
          placeholder="dave@yourbuildingco.com.au"
          className="wl-input"
          aria-invalid={errors.email ? "true" : undefined}
          aria-describedby={errors.email ? tid("email-err") : undefined}
          data-testid={`${testPrefix}-email-input`}
        />
        {errors.email && <p id={tid("email-err")} className="text-xs" style={{ color: "rgb(var(--wl-danger))" }} data-testid={`${testPrefix}-email-error`}>{errors.email}</p>}
      </div>
      <div className={`grid gap-3 ${compact ? "" : "sm:grid-cols-2"}`}>
        {!compact && (
          <div className="space-y-1.5">
            <Label htmlFor={tid("phone")} className="wl-label">Phone <span style={{ color: "rgb(var(--wl-muted))" }}>(optional)</span></Label>
            <Input
              id={tid("phone")}
              type="tel"
              inputMode="tel"
              value={form.phone}
              onChange={(e) => set("phone")(e.target.value)}
              autoComplete="tel"
              placeholder="04xx xxx xxx"
              className="wl-input"
              data-testid={`${testPrefix}-phone-input`}
            />
          </div>
        )}
        <div className="space-y-1.5">
          <Label htmlFor={tid("jobs")} className="wl-label">How many active jobs do you run?</Label>
          <Select value={form.active_jobs} onValueChange={set("active_jobs")}>
            <SelectTrigger
              id={tid("jobs")}
              className="wl-select-trigger"
              aria-invalid={errors.active_jobs ? "true" : undefined}
              aria-describedby={errors.active_jobs ? tid("jobs-err") : undefined}
              data-testid={`${testPrefix}-active-jobs-select`}
            >
              <SelectValue placeholder="Pick a range" />
            </SelectTrigger>
            <SelectContent className="wl-select-content">
              {JOB_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value} data-testid={`${testPrefix}-active-jobs-option-${o.value}`}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.active_jobs && <p id={tid("jobs-err")} className="text-xs" style={{ color: "rgb(var(--wl-danger))" }} data-testid={`${testPrefix}-active-jobs-error`}>{errors.active_jobs}</p>}
        </div>
      </div>
      {/* honeypot — hidden from people, tempting to bots */}
      <div className="absolute -left-[9999px] top-auto h-px w-px overflow-hidden" aria-hidden="true">
        <label htmlFor={tid("website")}>Website</label>
        <input id={tid("website")} tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => set("website")(e.target.value)} />
      </div>
      {failed && (
        <p className="rounded-md px-3 py-2 text-sm" role="alert" style={{ background: "rgba(249,112,102,0.12)", color: "rgb(var(--wl-danger))" }} data-testid={`${testPrefix}-error-state`}>
          {failed}
        </p>
      )}
      <Button type="submit" className="wl-btn-primary w-full" disabled={busy} data-testid={`${testPrefix}-submit-button`}>
        {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <HardHat className="mr-2 h-4 w-4" />}
        {busy ? "Adding you…" : "Join the waitlist"}
      </Button>
      <p className="text-center text-xs sm:text-sm" style={{ color: "rgb(var(--wl-text-2))" }} data-testid={compact ? "footer-waitlist-discount-note" : "waitlist-discount-note"}>
        Early access members lock in a <span className="font-semibold wl-accent-text">20% lifetime discount</span>.
      </p>
    </form>
  );
};

const Wordmark = ({ size = "md", testId = "landing-wordmark" }) => (
  <span className={`inline-flex items-center ${size === "lg" ? "gap-3" : "gap-2.5"} select-none`} aria-label="BuiltOS" data-testid={testId}>
    <BuiltOSMark className={`${size === "lg" ? "h-10 w-10 rounded-[11px]" : "h-8 w-8 rounded-[9px]"} shrink-0 shadow-sm`} />
    <span className={`wl-heading font-extrabold leading-none ${size === "lg" ? "text-2xl" : "text-xl"}`}>
      <span style={{ color: "rgb(var(--wl-text))" }}>Built</span>
      <span className="wl-accent-text">OS</span>
    </span>
  </span>
);

const SectionHeading = ({ children, kicker, testId }) => (
  <div className="max-w-2xl">
    {kicker && (
      <p className="wl-accent-text mb-3 text-[11px] font-semibold uppercase tracking-[0.18em]">
        {kicker}
      </p>
    )}
    <h2 className="wl-heading text-2xl font-extrabold tracking-tight sm:text-3xl lg:text-4xl" data-testid={testId}>
      {children}
    </h2>
  </div>
);

export default function WaitlistPage() {
  useEffect(() => {
    const prev = document.title;
    document.title = "BuiltOS — Run your jobs. Protect your profit.";
    return () => { document.title = prev; };
  }, []);

  return (
    <div className="builtos-waitlist min-h-screen" data-testid="waitlist-landing-page">
      {/* ---------------- Hero ---------------- */}
      <header className="relative overflow-hidden">
        <div className="wl-grid pointer-events-none absolute inset-0" aria-hidden="true" />
        <div className="wl-hero-wash pointer-events-none absolute inset-0" aria-hidden="true" />
        <div className="relative mx-auto max-w-6xl px-4 pb-16 pt-6 sm:px-6 sm:pb-24 sm:pt-8">
          <div className="flex items-center justify-between gap-4">
            <Wordmark />
            <div className="flex items-center gap-3">
              <span className="wl-text-2 hidden text-sm sm:inline" data-testid="landing-sign-in-prompt">Already a member?</span>
              <Button asChild className="wl-btn-primary wl-btn-signin" data-testid="landing-sign-in-link">
                <Link to="/auth">
                  Sign in <ArrowRight className="ml-1.5 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>

          <div className="mt-14 grid items-start gap-10 lg:mt-20 lg:grid-cols-[1.1fr_0.9fr] lg:gap-14">
            <Reveal className="is-in">
              <p className="wl-chip mb-5 inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em]" data-testid="landing-kicker">
                <HardHat className="h-3.5 w-3.5" /> Built for Australian builders
              </p>
              <h1 className="wl-h1 wl-heading" data-testid="landing-headline">
                Run your jobs.
                <br />
                Protect your <span className="wl-highlight">profit.</span>
              </h1>
              <p className="mt-6 max-w-prose text-base leading-relaxed sm:text-lg" style={{ color: "rgb(var(--wl-text-2))" }} data-testid="landing-subheading">
                BuiltOS is the all-in-one ops platform built by a builder, for builders. Actuals, invoices, procurement, schedules, client portal — all in one place.
              </p>
              <ul className="mt-8 grid gap-3 text-sm sm:grid-cols-2" style={{ color: "rgb(var(--wl-text-2))" }} data-testid="landing-hero-points">
                {["One place for every job's numbers", "Invoices approved, not chased", "Clients updated without the 10pm call", "Early access: 20% off for life"].map((t) => (
                  <li key={t} className="flex items-start gap-2">
                    <BadgeCheck className="wl-accent-text mt-0.5 h-4 w-4 shrink-0" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </Reveal>

            <Reveal className="is-in">
              <div className="wl-card relative p-5 sm:p-7" data-testid="waitlist-hero-card">
                <div className="mb-5 flex items-center justify-between gap-3">
                  <div>
                    <h2 className="wl-heading text-lg font-bold">Join the waitlist</h2>
                    <p className="text-xs sm:text-sm" style={{ color: "rgb(var(--wl-muted))" }}>Takes 20 seconds. No card, no spam.</p>
                  </div>
                  <span className="wl-chip rounded-md px-2 py-1 text-[11px] font-bold uppercase tracking-wide">
                    20% off for life
                  </span>
                </div>
                <WaitlistForm variant="hero" source="home-hero" idPrefix="wl-hero" />
              </div>
            </Reveal>
          </div>
        </div>
      </header>

      {/* ---------------- Problem ---------------- */}
      <section className="wl-hairline border-t" style={{ background: "rgb(var(--wl-bg-2))" }} data-testid="landing-problem-section">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20 lg:py-24">
          <Reveal>
            <SectionHeading kicker="The daily grind" testId="landing-problem-heading">Sound familiar?</SectionHeading>
          </Reveal>
          <div className="mt-10 grid gap-4 md:grid-cols-3">
            {PAIN_POINTS.map((text, i) => (
              <Reveal key={text} className="wl-card wl-card-lift wl-card-accent-top p-6" data-testid="pain-point-card">
                <p className="wl-heading text-sm font-bold tabular-nums" style={{ color: "rgb(var(--wl-muted))" }}>0{i + 1}</p>
                <p className="mt-4 text-base font-medium leading-relaxed sm:text-lg" data-testid="pain-point-heading">{text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- Features ---------------- */}
      <section data-testid="landing-features-section">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20 lg:py-24">
          <Reveal>
            <SectionHeading kicker="What's inside" testId="landing-features-heading">Everything a builder actually needs.</SectionHeading>
          </Reveal>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map(({ icon: Icon, label, text }) => (
              <Reveal key={label} className="wl-card wl-card-lift flex gap-4 p-5" data-testid="feature-tile">
                <span className="wl-icon-well flex h-11 w-11 shrink-0 items-center justify-center rounded-lg">
                  <Icon className="h-5 w-5" />
                </span>
                <div className="min-w-0">
                  <h3 className="wl-heading text-base font-bold">{label}</h3>
                  <p className="mt-1 text-sm leading-relaxed" style={{ color: "rgb(var(--wl-text-2))" }}>{text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- Pricing ---------------- */}
      <section className="wl-hairline border-t" style={{ background: "rgb(var(--wl-bg-2))" }} data-testid="landing-pricing-section">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20 lg:py-24">
          <Reveal>
            <SectionHeading kicker="Pricing" testId="landing-pricing-heading">Simple pricing. No surprises.</SectionHeading>
          </Reveal>
          <div className="mt-10 grid items-stretch gap-4 lg:grid-cols-3 lg:gap-5">
            {TIERS.map((t) => (
              <Reveal
                key={t.key}
                className={`wl-card relative flex flex-col p-6 sm:p-7 ${t.popular ? "wl-card-popular lg:scale-[1.03]" : ""}`}
                data-testid="pricing-tier-card"
                data-tier={t.key}
              >
                {t.popular && (
                  <span className="absolute -top-3 left-6 rounded-md px-2 py-1 text-[11px] font-bold uppercase tracking-wide" style={{ background: "rgb(var(--wl-accent))", color: "rgb(var(--wl-on-accent))" }} data-testid="pricing-most-popular">
                    Most popular
                  </span>
                )}
                <h3 className="wl-heading text-lg font-bold" data-testid={`pricing-tier-name-${t.key}`}>{t.name}</h3>
                <p className="mt-4 flex items-baseline gap-1">
                  <span className="wl-heading text-4xl font-extrabold tabular-nums tracking-tight" data-testid={`pricing-tier-price-${t.key}`}>${t.price}</span>
                  <span className="text-sm" style={{ color: "rgb(var(--wl-muted))" }}>/month</span>
                </p>
                <p className="mt-3 text-sm leading-relaxed sm:text-base" style={{ color: "rgb(var(--wl-text-2))" }}>{t.blurb}</p>
                <p className="mt-auto pt-6 text-xs sm:text-sm" style={{ color: "rgb(var(--wl-muted))" }} data-testid={`pricing-tier-discount-${t.key}`}>
                  Waitlist members get <span className="font-semibold wl-accent-text">20% off</span> — locked in for life.
                </p>
              </Reveal>
            ))}
          </div>
          <p className="mt-6 text-xs" style={{ color: "rgb(var(--wl-muted))" }}>All prices in AUD, excluding GST.</p>
        </div>
      </section>

      {/* ---------------- Footer ---------------- */}
      <footer className="wl-hairline border-t" data-testid="landing-footer">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20">
          <div className="grid gap-10 lg:grid-cols-[1fr_1fr] lg:gap-14">
            <div>
              <Wordmark size="lg" testId="footer-wordmark" />
              <p className="wl-heading mt-5 text-2xl font-extrabold tracking-tight sm:text-3xl" data-testid="footer-tagline">Built by a builder, for builders.</p>
              <p className="mt-3 max-w-prose text-sm leading-relaxed sm:text-base" style={{ color: "rgb(var(--wl-text-2))" }}>
                Early access opens in waves. Join now and your 20% lifetime discount is locked in the day you sign up.
              </p>
              <p className="mt-8 inline-flex items-center gap-2 text-xs" style={{ color: "rgb(var(--wl-muted))" }}>
                <MessageSquare className="h-3.5 w-3.5" /> Questions? Reply to your confirmation email.
              </p>
            </div>
            <div className="wl-card p-5 sm:p-7" data-testid="footer-waitlist-card">
              <h3 className="wl-heading text-lg font-bold">Get on the list</h3>
              <p className="mb-5 text-xs sm:text-sm" style={{ color: "rgb(var(--wl-muted))" }}>We'll email you when your spot opens.</p>
              <WaitlistForm variant="footer" source="home-footer" idPrefix="wl-footer" />
            </div>
          </div>
          <div className="wl-hairline wl-muted mt-12 flex flex-col gap-2 border-t pt-6 text-xs sm:flex-row sm:items-center sm:justify-between">
            <span>© {new Date().getFullYear()} BuiltOS. Made in Australia.</span>
            <span className="inline-flex items-center gap-1.5"><ClipboardList className="h-3.5 w-3.5" /> No spam. Unsubscribe any time.</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
